<template>
	<div>
		<h2 class="content-block">
			<img src="@/assets/reg_form_header.png" class="header-image" />
			<div class="header-title"></div>
		</h2>

		<DxPopup v-model:visible="videoPopup" :shading="true" shadingColor="#ccca" :showTitle="true" :showCloseButton="true" :dragEnabled="false" :hideOnOutsideClick="true" :wrapper-attr="{ class: 'welcome-popup' }"
			max-width="480px" max-height="832px" :fullScreen="smallScreenVideo"
			:onShown="videoPopupToggled" :onHiding="videoPopupToggled">
			<video controls src="../assets/AlvinBH.mp4" ref="welcomeVideo" width="100%" height="100%"/>
		</DxPopup>

		<DxPopup v-model:visible="submitError" @hidden="submitError = false" title="Error"
			:shading="true" shadingColor="#ccca" :showTitle="true" :showCloseButton="true" :dragEnabled="false" :hideOnOutsideClick="true"
			max-width="400px" max-height="280px">
			<div class="submitsurvey-error-body">{{ submitErrorMsg }}</div>
		</DxPopup>

		<div v-if="noApplications" class="content-block dx-card responsive-paddings onboarding-form">
			<div class="no-application-error">
				<i class="dx-icon-warning" style="font-size: 48px; color: #d9534f;"></i>
				<h3>Application Required</h3>
				<p>You must submit an application before onboarding to COLPUNO.</p>
				<p>Please complete your application first, then return to this page to continue with the onboarding process.</p>
			</div>
		</div>

		<div v-else-if="notNursingCourse" class="content-block dx-card responsive-paddings onboarding-form">
			<div class="not-nursing-warning">
				<i class="dx-icon-info" style="font-size: 48px; color: #f0ad4e;"></i>
				<h3>Nursing Students Only</h3>
				<p>Only nursing students are currently accepted to COLPUNO.</p>
				<p>Thank you for your interest in COLPUNO - we'll contact you if we expand our platform to other professions!</p>
			</div>
		</div>

		<div v-else ref="onboardingFormContainer" class="content-block dx-card responsive-paddings onboarding-form">
			<DxBox direction="col" width="100%" class="simple-item-descriptive dx-card dx-theme-accent-as-border-color" v-if="showIntro">
				<DxItem :ratio="0" baseSize="auto">
					<p>You're almost there!</p>
					<p>Let's get you on COLPUNO 🚀 the platform trusted by thousands of Filipino nurses to jumpstart their careers.</p>
					<p>COLPUNO is the first and only digital career companion designed to help Filipino nurses grow their careers locally and globally. It helps you discover your strengths, build your profile, and get matched with the right, trusted job openings— in the Philippines and abroad.</p>
					<p>Watch here why Alvin Andrade, Brainhub Review Centers' Founder and CEO, recommend you to join COLPUNO right away:</p>
				</DxItem>
				<DxItem :ratio="0" :baseSize="smallScreenVideo ? 300 : 280" style="display: flex; justify-content: center;">
					<div class="simple-item-videobox" @click="videoPopup = true" style="max-width: 300px;">
						<div><i style="font-size:48px;" class="dx-icon-video"></i></div>
						<span>Play Video</span>
					</div>
				</DxItem>
			</DxBox>
			<div v-if="showIntro" style="text-align: center; margin: 20px 0; padding: 20px; display: flex; align-items: center; justify-content: center; gap: 15px;">
				<DxButton text="Continue Onboarding" type="default" @click="continueToOnboarding" />
				<!--<a href="https://www.colpuno.com/pci-npc-registration-certificate-2025.jpg" target="_blank" rel="noopener noreferrer" style="display: inline-block;">
					<img src="@/assets/pci-registration-seal.png" alt="PCI Registration Seal" style="height: auto; max-height: 80px; width: auto;" />
				</a>-->
			</div>
			<div v-else-if="showInviteSection" class="colpuno-invite-block">
				<p v-if="inviteSuccess" class="colpuno-invite-success">You're all set. Check your email <strong>{{ inviteEmail }}</strong> for next steps from COLPUNO.</p>
				<template v-else>
					<p class="colpuno-invite-copy">Tap the button below to request your COLPUNO account.<br>We'll use the email <strong>{{ inviteEmail }}</strong> from your Brainhub application.</p>
					<div style="text-align: center; margin-top: 16px;">
						<DxButton
							text="Request COLPUNO access"
							type="default"
							:disabled="inviteLoading || !inviteEmail"
							@click="requestColpunoInvite"
						/>
					</div>
				</template>
			</div>
		</div>
	</div>
</template>

<script setup>
import { DxBox, DxItem } from 'devextreme-vue/box';
import DxPopup from "devextreme-vue/popup"
import DxButton from "devextreme-vue/button"
import { inject, ref, watch, nextTick, computed } from "vue";
import { sizes } from '../utils/media-query';
import appInfo from '@/app-info.js';

const $http = inject('$http');

const USER_INVITATIONS_URL = appInfo.PUBLIC_COLPUNO_USER_INVITATIONS_URL;
const SOURCE_BRAINHUB_SSO = 'brainhub_sso';

/** Extract an error message from a parsed JSON error body. */
function parseErrorBody(errorData) {
	if (typeof errorData === 'string') return errorData;
	if (!errorData || typeof errorData !== 'object') return null;
	if (typeof errorData.detail === 'string') return errorData.detail;
	if (Array.isArray(errorData.violations)) {
		const violations = errorData.violations
			.map((v) =>
				v.propertyPath && v.message
					? `${v.propertyPath}: ${v.message}`
					: v.message || '',
			)
			.filter(Boolean)
			.join(', ');
		if (violations) return violations;
	}
	if (typeof errorData['hydra:description'] === 'string') return errorData['hydra:description'];
	if (typeof errorData.message === 'string') return errorData.message;
	if (errorData.error) {
		if (typeof errorData.error === 'string') return errorData.error;
		if (
			typeof errorData.error === 'object' &&
			errorData.error !== null &&
			typeof errorData.error.message === 'string'
		)
			return errorData.error.message;
	}
	return null;
}

/** Handle fetch-level errors (network, TypeError, etc.). */
function handleFetchError(error) {
	if (error instanceof TypeError && error.message.includes('fetch')) {
		return {
			error:
				'Unable to connect to the server. Please check your internet connection and try again.',
		};
	}
	return {
		error:
			error instanceof Error
				? error.message
				: 'An unexpected error occurred. Please try again later.',
	};
}

const videoPopup = ref(false);
const welcomeVideo = ref(null);
const smallScreenVideo = sizes()['screen-x-small'] || sizes()['screen-small'];
const existingApplication = ref(null);
const showIntro = ref(true);
const showInviteSection = ref(false);
const noApplications = ref(false);
const notNursingCourse = ref(false);
const onboardingFormContainer = ref(null);
const inviteLoading = ref(false);
const inviteSuccess = ref(false);

const inviteEmail = computed(() => existingApplication.value?.email || '');

let submitError = ref(false);
let submitErrorMsg = ref('');

const videoPopupToggled = () => {
	if (videoPopup.value) {		// Video was just opened
		welcomeVideo.value.play();
	} else {					// Video was just closed
		welcomeVideo.value.pause();
	}
};

const continueToOnboarding = () => {
	showIntro.value = false;
	showInviteSection.value = true;
};

// Scroll into view when the invite step is shown
watch(showInviteSection, async (newValue) => {
	if (newValue) {
		await nextTick();
		if (onboardingFormContainer.value) {
			onboardingFormContainer.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}
});

const requestColpunoInvite = async () => {
	const email = inviteEmail.value;
	if (!email) {
		submitErrorMsg.value = 'No email found for your application.';
		submitError.value = true;
		return;
	}
	inviteLoading.value = true;
	try {
		const response = await fetch(USER_INVITATIONS_URL, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/ld+json',
				Accept: 'application/ld+json',
			},
			body: JSON.stringify({ email, source: SOURCE_BRAINHUB_SSO }),
		});
		if (!response.ok) {
			let errorMessage = response.statusText || 'Something went wrong';
			try {
				const errorData = await response.json();
				const parsed = parseErrorBody(errorData);
				if (parsed) errorMessage = parsed;
			} catch { /* use statusText */ }
			submitErrorMsg.value = errorMessage;
			submitError.value = true;
			return;
		}
		await response.json().catch(() => {});
		inviteSuccess.value = true;
	} catch (error) {
		const { error: msg } = handleFetchError(error);
		submitErrorMsg.value = msg;
		submitError.value = true;
	} finally {
		inviteLoading.value = false;
	}
};

// At startup, load application details (email, course) for COLPUNO invite eligibility.
$http.get('/user/me?show_application_details=1').then((response) => {
	if (Array.isArray(response.data.applications) && response.data.applications.length >= 1) {
		existingApplication.value = response.data.applications[0];
		noApplications.value = false;
		if (existingApplication.value.course !== 'NLE') {
			notNursingCourse.value = true;
		} else {
			notNursingCourse.value = false;
		}
	} else {
		existingApplication.value = null;
		noApplications.value = true;
	}
});

</script>

<style lang="scss">
.header-image {
	width: 100%;
	max-width: 1200px;
}
.header-title {
	text-align: center;
	text-transform: uppercase;
	width: 100%; max-width: 1200px;
	border-bottom: 4px double black;
}
.header-subtitle {
	text-align: center;
	width: 100%; max-width: 1200px;
	font-size: 20px;
}
.onboarding-form {
	max-width: 1200px;
	
	.simple-item-descriptive {
		padding: 0 20px;
		border-left: 6px solid;
		text-align: justify;
	}
	.simple-item-descriptive p {
		padding-right: 15px;
	}
	.simple-item-descriptive p+p {
		margin-top: 0;
	}
	.simple-item-videobox {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;
		color: #efefef;
		margin: 10px;
		font-size: 24px;
		text-align: center;
		background: #ccc;
		background-image: url(../assets/AlvinBH.th.jpg);
		background-size: cover;
		background-position: center;
		background-repeat: no-repeat;
		border: 2px solid #999;
		text-shadow: 2px 2px 3px #040404;
		cursor: pointer;
		width: 300px;
		height: 339px;
	}
	@media (min-width: 768px) {
		.simple-item-videobox {
			width: 450px;
			height: 509px;
		}
	}
}

.colpuno-invite-block {
	padding: 20px;
	text-align: center;
	.colpuno-invite-copy {
		color: #666;
		max-width: 560px;
		margin: 0 auto;
		line-height: 1.5;
	}
	.colpuno-invite-success {
		color: #2d7a32;
		font-size: 18px;
		margin: 0;
	}
}

.dx-dateviewroller-day {
    display: none;
}
.welcome-popup .dx-popup-content {
	overflow: hidden;
}
.submitsurvey-error-body {
	white-space: pre-line;
	border-left: 2px solid red;
	padding-left: 15px;
}

.no-application-error {
	text-align: center;
	padding: 40px 20px;
	
	h3 {
		color: #d9534f;
		margin: 20px 0 10px;
		font-size: 24px;
	}
	
	p {
		color: #666;
		font-size: 16px;
		margin: 10px 0;
		max-width: 500px;
		margin-left: auto;
		margin-right: auto;
	}
}

.not-nursing-warning {
	text-align: center;
	padding: 40px 20px;
	
	h3 {
		color: #f0ad4e;
		margin: 20px 0 10px;
		font-size: 24px;
	}
	
	p {
		color: #666;
		font-size: 16px;
		margin: 10px 0;
		max-width: 500px;
		margin-left: auto;
		margin-right: auto;
	}
}

</style>
